/**
 * API Keys Management Page
 */

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  useDisclosure,
} from '@heroui/react';
import { createFileRoute } from '@tanstack/react-router';
import { Copy, Key, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '@/lib/api/hooks/auth';
import { formatRelativeTime } from '@/lib/design-system';

export const Route = createFileRoute('/settings/api-keys')({
  component: ApiKeysPage,
});

function ApiKeysPage() {
  const { data: apiKeys, isLoading } = useApiKeys();
  const createApiKey = useCreateApiKey();
  const revokeApiKey = useRevokeApiKey();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const handleCreateKey = async () => {
    try {
      const result = await createApiKey.mutateAsync({ name: newKeyName || 'API Key' });
      if (result?.key) {
        setCreatedKey(result.key);
      }
      setNewKeyName('');
    } catch {
      // Error handled by mutation
    }
  };

  const handleCopyKey = () => {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey);
    }
  };

  const handleCloseModal = () => {
    setCreatedKey(null);
    onClose();
  };

  return (
    <div className="container-page max-w-3xl py-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1>API Keys</h1>
          <p className="text-default-500">Manage your API keys for programmatic access</p>
        </div>
        <Button
          color="primary"
          startContent={<Plus className="h-4 w-4" />}
          onPress={onOpen}
        >
          Create Key
        </Button>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {!isLoading && apiKeys && apiKeys.length === 0 && (
        <Card>
          <CardBody className="flex flex-col items-center gap-4 py-12 text-center">
            <div className="rounded-full bg-default-100 p-4">
              <Key className="h-8 w-8 text-default-400" />
            </div>
            <div>
              <h3>No API keys yet</h3>
              <p className="text-default-500">
                Create an API key to use the Relay API programmatically.
              </p>
            </div>
            <Button
              color="primary"
              startContent={<Plus className="h-4 w-4" />}
              onPress={onOpen}
            >
              Create Key
            </Button>
          </CardBody>
        </Card>
      )}

      {!isLoading && apiKeys && apiKeys.length > 0 && (
        <Card>
          <CardHeader>
            <h2>Active Keys</h2>
          </CardHeader>
          <CardBody className="p-0">
            <Table
              aria-label="API keys table"
              removeWrapper
              classNames={{
                th: 'bg-transparent',
              }}
            >
              <TableHeader>
                <TableColumn>NAME</TableColumn>
                <TableColumn>KEY PREFIX</TableColumn>
                <TableColumn>CREATED</TableColumn>
                <TableColumn>ACTIONS</TableColumn>
              </TableHeader>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell>
                      <p className="font-medium">{key.name || 'API Key'}</p>
                    </TableCell>
                    <TableCell>
                      <code className="rounded bg-default-100 px-2 py-1 text-sm">
                        {key.key_prefix}...
                      </code>
                    </TableCell>
                    <TableCell className="text-default-500">
                      {formatRelativeTime(new Date(key.created_at))}
                    </TableCell>
                    <TableCell>
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        color="danger"
                        onPress={() => revokeApiKey.mutate(key.id)}
                        isLoading={revokeApiKey.isPending}
                        aria-label="Revoke key"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      )}

      {/* Create Key Modal */}
      <Modal isOpen={isOpen} onClose={handleCloseModal}>
        <ModalContent>
          {createdKey ? (
            <>
              <ModalHeader>API Key Created</ModalHeader>
              <ModalBody>
                <p className="text-default-500 mb-4">
                  Copy your API key now. You won't be able to see it again.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded bg-default-100 p-3 text-sm break-all">
                    {createdKey}
                  </code>
                  <Button
                    isIconOnly
                    variant="flat"
                    onPress={handleCopyKey}
                    aria-label="Copy key"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="primary" onPress={handleCloseModal}>
                  Done
                </Button>
              </ModalFooter>
            </>
          ) : (
            <>
              <ModalHeader>Create API Key</ModalHeader>
              <ModalBody>
                <Input
                  label="Key Name"
                  labelPlacement="outside"
                  placeholder="e.g., Production API"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                />
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onPress={handleCreateKey}
                  isLoading={createApiKey.isPending}
                >
                  Create
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
